import { ITargetAudience } from '@dadohr/shared/domainTypes/targetAudience';
import { Doc } from '@dadohr/shared/domainTypes/document';
import styled from '@emotion/styled';
import { Button, Card, Typography } from '@material-ui/core';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../components/AdditionalActionsMenu';
import { DeletionConfirmationDialogBody } from '../../../../../components/DeletionConfirmation';
import { Loader } from '../../../../../components/Loader';
import { NoAccess } from '../../../../../components/NoAccess';
import { useUnsafeTranslation } from '../../../../../i18next';
import { PageBody } from '../../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../../layout/PageToolbar';
import { useRouter } from '../../../../../hooks/useRouter';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useRoutes } from '../../../../../routes';
import { useAdminUserById } from '../../../../../services/adminUsers';
import { isDadoEmailAddress, useCurrentUser } from '../../../../../services/currentUser';
import { hasPermissionToAccessPage } from '../../../../../services/permissions';
import { captureException } from '../../../../../sentry';
import {
  createTargetAudience,
  deleteTargetAudience,
  useAllTargetAudiences
} from '../../../../../services/targetAudience';
import { RuleRenderer, RuleBuilderConfig } from '../../../../Experiences/components/RuleBuilder';
import { UserField } from '@dadohr/shared/domainTypes/experience';
import { useUserRuleConfigWithAutocompletion } from '../../../../../services/user';
import { useUsersSideNav } from '../../sideNav';

const HeaderRow = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 4px;
`;

const GridRow = styled('div')`
  display: grid;
  grid-template-columns: 1.4fr 2fr 1fr 1fr 48px;
  align-items: center;
  column-gap: 12px;
`;

const HeaderCell = styled(Typography)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  text-transform: uppercase;
`;

const DataRow = styled(GridRow)`
  padding: 14px 4px;
  border-bottom: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.custom.colors.gray.w100};
  }
`;

const TargetAudienceRow = ({
  audience,
  config,
  onOpen
}: {
  audience: Doc<ITargetAudience>;
  config: RuleBuilderConfig<UserField>;
  onOpen: (audienceId: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const [updatedBy] = useAdminUserById(org.id, audience.data.updatedByUserId);
  const editor = updatedBy?.data.email ? (isDadoEmailAddress(updatedBy?.data.email) ? 'Dado Customer Support' : updatedBy?.data.email) : '';

  const options: AdditionActionsMenuOption[] = useMemo(
    () => [
      {
        key: 'edit',
        label: t('Edit'),
        onClick: () => onOpen(audience.id)
      },
      {
        key: 'duplicate',
        label: t('Duplicate'),
        divider: true,
        // No duplicate endpoint exists; a copy is just a create with the same
        // rule. Open the copy in the editor right away, since duplicating is
        // usually the first step of adjusting it into a new segment.
        onClick: () =>
          createTargetAudience({
            orgId: org.id,
            name: `Copy of ${audience.data.name}`,
            rule: audience.data.rule
          })
            .then(({ id }) => {
              enqueueSnackbarSuccess(t('Saved segment duplicated.'));
              onOpen(id);
            })
            .catch((err) => {
              captureException(err, {
                tags: { action: 'duplicate_target_audience' },
                extra: { targetAudienceId: audience.id }
              });
              enqueueSnackbarError(
                err?.message || t('Failed to duplicate saved segment.')
              );
            })
      },
      {
        key: 'delete',
        label: t('Delete'),
        dialog: ({ onClose }) => (
          <DeletionConfirmationDialogBody
            onClose={onClose}
            dialogTitle={t('Delete Saved Segment')}
            buttonAction={t('Delete Saved Segment')}
            dialogBody={() => (
              <Typography component="p">
                {t(
                  'Your saved segment will be permanently deleted. Any uses of this segment will not be deleted.'
                )}
              </Typography>
            )}
            onConfirm={() =>
              deleteTargetAudience(audience.id)
                .then(() => {
                  enqueueSnackbarSuccess(t('Saved segment deleted.'));
                })
                .catch((err) => {
                  captureException(err, {
                    tags: { action: 'delete_target_audience' },
                    extra: { targetAudienceId: audience.id }
                  });
                  enqueueSnackbarError(
                    err?.message || t('Failed to delete saved segment.')
                  );
                  // Keep the dialog open on failure; the body only closes
                  // itself once onConfirm resolves.
                  throw err;
                })
            }
          />
        )
      }
    ],
    [audience, org.id, onOpen, t, enqueueSnackbarSuccess, enqueueSnackbarError]
  );

  return (
    <DataRow onClick={() => onOpen(audience.id)}>
      <Typography variant="body1">{audience.data.name}</Typography>
      <Typography variant="body2" color="textSecondary">
        <RuleRenderer value={audience.data.rule} config={config} variant="oneline" />
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {audience.data.updatedAt?.toDate().toLocaleDateString() ?? ''}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {editor}
      </Typography>
      {/* The menu's confirm dialog renders through a portal, but React click
          events still bubble to DataRow. Stop them here so Cancel / Delete
          inside the dialog never navigates to the editor. */}
      <div onClick={(ev) => ev.stopPropagation()}>
        <AdditionalActionsMenu
          variant="horizontal"
          label={t('More actions')}
          options={options}
        />
      </div>
    </DataRow>
  );
};

const TargetAudiencesListContent = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { ROUTES } = useRoutes();
  const { history } = useRouter();
  const sideNav = useUsersSideNav();
  const [audiences, loading] = useAllTargetAudiences(org.id);
  const config = useUserRuleConfigWithAutocompletion(
    org.id,
    org.customUserFields,
    org.tz
  );

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('Target audiences')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions />
      </PageToolbar>

      <Card elevation={0} style={{ padding: 24 }}>
        <HeaderRow>
          <div>
            <Typography variant="h3" style={{ marginBottom: 6 }}>
              {t('Saved Segment Library')}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ maxWidth: 520 }}
            >
              {t(
                'Saved segments allow you to create filters for your employees that you can then copy to your experiences. Updates here will NOT populate to places where you have copied any saved segments.'
              )}
            </Typography>
          </div>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => history.push(ROUTES.users.targetAudiences.new.url())}
          >
            {t('New audience')}
          </Button>
        </HeaderRow>

        <GridRow style={{ padding: '10px 4px', borderBottom: '1px solid #EBEDEF' }}>
          <HeaderCell>{t('NAME')}</HeaderCell>
          <HeaderCell>{t('DESCRIPTION')}</HeaderCell>
          <HeaderCell>{t('LAST UPDATED')}</HeaderCell>
          <HeaderCell>{t('UPDATED BY')}</HeaderCell>
          <div />
        </GridRow>

        {loading && <Loader height={200} />}

        {!loading &&
          audiences?.map((aud) => (
            <TargetAudienceRow
              key={aud.id}
              audience={aud}
              config={config}
              onOpen={(id) => history.push(ROUTES.users.targetAudiences.edit.url(id))}
            />
          ))}
      </Card>
    </PageBody>
  );
};

export const PageTargetAudiencesList = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.targetAudiences.list)) {
    return <NoAccess />;
  }

  return <TargetAudiencesListContent />;
};
